import React, { FC, Fragment, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import { debounce, LoadingOverlay } from "cayo.ui";
import { ServerError } from "@cayo/shared/components/App/types";
import logger from "@cayo/shared/libs/logger";
import { useServerState } from "./hook";
import ServerUnavailable from "./ServerUnavailable";

interface IServerStateComponent {
  serverError?: ServerError | undefined | null;
  onServerUnavailable: (error: ServerError | null | undefined) => void;
}

const restartServiceTimeout = 60000; // 1 min

const ServerStateComponent: FC<IServerStateComponent> = ({
  serverError: serverUnavailableError,
  onServerUnavailable,
}) => {
  const intl = useIntl();

  const { isLoading, healthCheckError, setServiceState, runHealthCheck } = useServerState();

  useEffect(() => {
    log.debug("health check error", healthCheckError);

    switch (healthCheckError?.state) {
      case "restarting":
        log.debug("Server is restarting");
        debounce(() => {
          setServiceState("stopped");
        }, restartServiceTimeout)();

        const error = healthCheckError?.message
          ? healthCheckError
          : ({
              message: intl.formatMessage(appStateMessages.serverRestarting),
              state: "restarting",
            } as ServerError);

        log.debug("onServerUnavailable called with error", error);
        onServerUnavailable(error);
        break;

      case "stopped":
        log.debug("Server is stopped");
        onServerUnavailable({ message: "", state: "stopped" });
        break;
      case "stopping":
        {
          log.debug("Server is stopping");
          const error: ServerError = healthCheckError?.message
            ? healthCheckError
            : { message: intl.formatMessage(appStateMessages.serverStopping), state: "stopping" };

          log.debug("onServerUnavailable called with error", error);
          onServerUnavailable(error);
        }
        break;

      case "starting":
        {
          log.debug("Server is starting");
          const error: ServerError = healthCheckError?.message
            ? healthCheckError
            : { message: intl.formatMessage(appStateMessages.serverStarting), state: "starting" };

          log.debug("onServerUnavailable called with error", error);
          onServerUnavailable(error);
        }
        return;

      case "started":
        log.debug("Server is started");
        if (serverUnavailableError && healthCheckError?.operatingMode === "normal") {
          log.debug("reload page, server started", healthCheckError);
          document.location.reload();
          break;
        }

        if (healthCheckError?.operatingMode !== "normal") {
          const error: ServerError = healthCheckError?.message
            ? healthCheckError
            : {
                message: intl.formatMessage(appStateMessages.serverNotOperationalMode),
                state: "none",
                operatingMode: healthCheckError?.operatingMode,
              };

          log.debug("onServerUnavailable called with error", error);
          onServerUnavailable(error);
        } else {
          log.debug("onServerUnavailable called with error", null);
          onServerUnavailable(null);
        }
        break;

      case "failed":
        log.debug("Server failed to start", healthCheckError);
        {
          const error: ServerError = healthCheckError?.message
            ? healthCheckError
            : {
                message: intl.formatMessage(appStateMessages.serverFailed),
                isCritical: true,
                state: "failed",
              };

          log.debug("onServerUnavailable called with error", error);
          onServerUnavailable(error);
        }
        break;
      default:
        if (healthCheckError?.state) {
          log.debug("Unhandled server state", healthCheckError?.state, healthCheckError);
        }
        break;
    }
  }, [
    healthCheckError?.message,
    healthCheckError?.description,
    healthCheckError?.state,
    healthCheckError?.operatingMode,
  ]);

  useEffect(() => {
    log.debug("onServerUnavailable called with healthCheckError", healthCheckError);
    onServerUnavailable(healthCheckError);
  }, [healthCheckError]);

  if (isLoading) {
    return <LoadingOverlay />;
  }

  return serverUnavailableError ? (
    <ServerUnavailable
      key={serverUnavailableError?.message ?? "ServerUnavailable"}
      error={serverUnavailableError}
      onRetry={runHealthCheck}
    />
  ) : (
    <Fragment />
  );
};

const appStateMessages = defineMessages({
  serverRestarting: {
    defaultMessage: "The service is being restarted, please wait.",
    id: "app-state-messages.server-is-being-restarted",
  },
  serverStarting: {
    defaultMessage: "The service is being started, please wait.",
    id: "app-state-messages.server-is-being-started",
  },
  serverStopping: {
    defaultMessage: "The service is being stopped, please wait.",
    id: "app-state-messages.server-is-being-stopped",
  },
  serverFailed: {
    defaultMessage: "Service failed to start.",
    id: "app-state-messages.server-failed-to-start",
  },

  serverNotOperationalMode: {
    defaultMessage: "The service is currently not operational.",
    id: "app-state-messages.server-not-operational",
  },
});

const log = logger.getLogger("ServerStateComponent");

export default ServerStateComponent;
